import { config } from '../config.js'
import jwtDecode from 'jwt-decode'

function isFunc(variable) {
  return typeof variable === 'function'
}

// using https://github.com/aws-amplify/amplify-js/tree/master/packages/amazon-cognito-identity-js
class Authentication {
  constructor() {
    // userPoolId and clientId alone are not enough to do any
    // malicious activity on your user pool
    this.poolData = {
      UserPoolId: config.userPoolId, // Your user pool id here
      ClientId: config.clientId, // Your client id here
      Storage: new AmazonCognitoIdentity.CookieStorage({
        domain: config.domainAdmin,
        secure: config.secure,
      }),
    }
    this.userPool = new AmazonCognitoIdentity.CognitoUserPool(this.poolData)
  }

  authenticate(callback) {
    var user = document.getElementById('email').value
    var pw = document.getElementById('password').value
    var authenticationDetails = new AmazonCognitoIdentity.AuthenticationDetails(
      {
        Username: user,
        Password: pw,
      },
    )

    var cognitoUser = this.getUser(user)
    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: function (result) {
        if (isFunc(callback)) callback(true)
      },

      onFailure: function (err) {
        var message = err.message || JSON.stringify(err)
        if (isFunc(callback)) callback(false, message)
      },
    })
  }

  getUser(username) {
    return new AmazonCognitoIdentity.CognitoUser({
      Username: username,
      Pool: this.userPool,
      Storage: new AmazonCognitoIdentity.CookieStorage({
        domain: config.domainAdmin,
        secure: config.secure,
      }),
    })
  }

  getAuthenticatedUser() {
    var cognitoUser = this.userPool.getCurrentUser()
    if (!cognitoUser) {
      return null
    }

    return cognitoUser.username
  }

  // Updates the given cognito user attribute for the current user
  updateUserDetails(attrName, attrValue, callback) {
    var cognitoUser = this.userPool.getCurrentUser()

    // Let's just do one at a time for now
    var attributeList = [
      new AmazonCognitoIdentity.CognitoUserAttribute({
        Name: attrName,
        Value: attrValue,
      }),
    ]

    // NOTE: getSession must be called to authenticate
    // user before updating their attributes
    cognitoUser.getSession((err, result) => {
      if (err) {
        console.log(err.message || JSON.stringify(err))
        if (isFunc(callback)) callback(false)
      }

      // Attempt to save attribute to cognito user
      cognitoUser.updateAttributes(attributeList, (err, result) => {
        this.sendResult(callback, err)
      })
    })
  }

  // Fetches all cognito user attributes for the current user.
  getUserDetails(callback) {
    let attributes = []

    // Fetch session data
    var cognitoUser = this.userPool.getCurrentUser()
    if (!cognitoUser) {
      return
    }

    // NOTE: getSession must be called to authenticate
    // user before calling getUserAttributes
    cognitoUser.getSession(function (err, session) {
      if (err) {
        console.log(err.message || JSON.stringify(err))
        return
      }

      // Fetch attributes related to the current user
      cognitoUser.getUserAttributes(function (err, result) {
        if (err) {
          console.log(err.message || JSON.stringify(err))
          return
        }

        // Building the attribute list
        for (let i = 0; i < result.length; i++) {
          attributes.push({
            name: result[i].getName(),
            value: result[i].getValue(),
          })
        }

        // Since the cognito functions are asyncronous, we need to
        // use callbacks to get the data back.
        if (isFunc(callback)) callback(attributes)
      })
    })
  }
  async getUserGroups(callback) {
    let groups = []

    // Fetch session data
    var cognitoUser = this.userPool.getCurrentUser()
    if (!cognitoUser) {
      return
    }

    // NOTE: getSession must be called to authenticate
    // user before calling getUserAttributes
    cognitoUser.getSession(function (err, session) {
      if (err) {
        console.log(err.message || JSON.stringify(err))
        return
      }
      var sessionIdInfo = jwtDecode(session.getIdToken().jwtToken)
      groups = sessionIdInfo['cognito:groups']
      // Since the cognito functions are asyncronous, we need to
      // use callbacks to get the data back.
      if (isFunc(callback)) callback(groups)
    })
  }

  // retrieves the idToken
  async getIdToken() {
    // Fetch session data
    var cognitoUser = this.userPool.getCurrentUser()
    if (!cognitoUser) {
      return
    }

    return new Promise(function (resolve, reject) {
      cognitoUser.getSession(function (err, session) {
        if (err) {
          console.error(
            `failed to get session ${err.message || JSON.stringify(err)}`,
          )
          return resolve(null)
        } else if (!session.isValid()) {
          console.error('invalid session')
          return resolve(null)
        } else {
          let token = session.getIdToken()
          if (token)
            return resolve({
              idToken: token.jwtToken,
              user: cognitoUser.username,
            })
        }
      })
    })
  }

  // Starts the forgot password process
  sendResetCode(callback) {
    var username = document.getElementById('email').value
    var cognitoUser = this.getUser(username)

    cognitoUser.forgotPassword({
      // Password reset process has started
      onSuccess: function (data) {},

      // Something went wrong
      onFailure: function (err) {
        var message = err.message || JSON.stringify(err)
        if (isFunc(callback)) callback(false, message)
      },

      // User has a verification code to use
      inputVerificationCode: function (err) {
        if (isFunc(callback)) callback(true)
      },
    })
  }

  // Reset your password with reset code
  resetPass(username, callback) {
    var code = document.getElementById('code').value
    var newPassword = document.getElementById('newPassword').value
    var cognitoUser = this.getUser(username)

    cognitoUser.confirmPassword(code, newPassword, {
      onSuccess() {
        if (isFunc(callback)) callback(true)
      },
      onFailure(err) {
        var message = err.message || JSON.stringify(err)
        if (isFunc(callback)) callback(false, message)
      },
    })
  }

  changePass(oldPass, newPass, callback) {
    var cognitoUser = this.userPool.getCurrentUser()
    cognitoUser.getSession((err, result) => {
      if (err) {
        console.log(err.message || JSON.stringify(err))
        if (isFunc(callback)) callback(false)
      }

      cognitoUser.changePassword(oldPass, newPass, (err, result) => {
        this.sendResult(callback, err)
      })
    })
  }

  // Logs the user out
  async logout() {
    var cognitoUser = this.userPool.getCurrentUser()
    if (cognitoUser) {
      await cognitoUser.signOut()
    }
  }

  registerUser(user, pass, callback) {
    this.userPool.signUp(user, pass, [], null, (err, result) => {
      this.sendResult(callback, err)
    })
  }

  deleteUser(callback) {
    var cognitoUser = this.userPool.getCurrentUser()
    cognitoUser.deleteUser((err, result) => {
      this.sendResult(callback, err)
    })
  }

  sendResult(callback, err) {
    if (isFunc(callback)) {
      if (err) {
        var message = err.message || JSON.stringify(err)
        callback(false, message)
        return
      }

      callback(true)
    }
  }
}

export let auth = new Authentication()
